import React, { useCallback, useRef, useState } from 'react';
import { AvTag, AvTagVariant } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Popover, SxProps, Theme, useTheme } from '@mui/material';
import { AvTooltip } from './AvTooltip';
import { findLayoutConstrainedAncestor, useDynamicVisibleItemsLimit } from './hooks';

const wrapperStyle = {
  minHeight: 32,
  display: 'contents',
  '> span:first-of-type': {
    lineHeight: '32px',
    alignSelf: 'start',
    flexShrink: 0,
  },
  '> *': {
    ...flex.itemsCenter,
  },
  a: {
    textDecoration: 'none',
  },
};

interface IAvTagsList {
  tags: string[];
  numOfTags?: number;
  getTagVariant?: (tag: string) => AvTagVariant;
  parentElementRef?: React.RefObject<HTMLElement | null>;
  maxTagWidth?: number;
}

interface TagItemProps {
  label: string;
  variant?: AvTagVariant;
  maxTagWidth?: number;
  containerProps?: { sx?: SxProps<Theme>; ref?: React.Ref<HTMLDivElement>; onMouseEnter?: () => void; onMouseLeave?: () => void };
}

const defaultTagItemSx = { paddingTop: '5px' };
const TagItem: React.FC<TagItemProps> = ({ label, variant, maxTagWidth, containerProps }) => (
  <Box
    sx={{ ...defaultTagItemSx, ...containerProps?.sx }}
    ref={containerProps?.ref}
    onMouseEnter={containerProps?.onMouseEnter}
    onMouseLeave={containerProps?.onMouseLeave}>
    <AvTooltip title={maxTagWidth ? label : ''}>
      <span>
        <AvTag
          oneLine
          text={label}
          variant={variant}
          sx={{
            display: 'inline-block',
            ...(maxTagWidth ? { maxWidth: maxTagWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
          }}
        />
      </span>
    </AvTooltip>
  </Box>
);

export const AvTagsList: React.FC<IAvTagsList> = ({ tags: tagsProp, numOfTags, getTagVariant, parentElementRef, maxTagWidth }) => {
  const tags = tagsProp ?? [];
  const theme = useTheme();
  const sx = {
    ...flex.row,
    ...ellipsis,
    WebkitLineClamp: '1',
    columnGap: 1,
    rowGap: '3px',
  };
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const isOverPopover = useRef(false);

  const resolveParent = useCallback(() => (rootRef.current ? findLayoutConstrainedAncestor(rootRef.current.parentElement) : null), []);

  const limit = useDynamicVisibleItemsLimit(rootRef, {
    fixedLimit: numOfTags,
    items: tags,
    overlapBetweenItems: 8,
    reservedSpaceForLastElement: 100,
    additionalPaddingPerItem: 12,
    parentElementRef,
    resolveParent: parentElementRef ? undefined : resolveParent,
  });
  const onMouseEnter = () => setAnchorEl(buttonRef.current);
  const handleClose = () => {
    if (!isOverPopover.current) {
      setAnchorEl(null);
    }
  };

  const open = Boolean(anchorEl);
  const hasOverflow = tags.length > limit;
  const shownItems = hasOverflow ? tags.slice(0, limit) : tags;
  const overflowItems = tags.slice(limit);

  const paperStyle = {
    ...flex.col,
    gap: 2,
    pl: 1,
    py: 2,
    pr: 4,
    backgroundColor: theme.palette.white.main,
    minWidth: 0,
  };

  return (
    <Box sx={wrapperStyle}>
      <Box sx={sx} ref={rootRef}>
        {shownItems.map(label => (
          <TagItem key={label} label={label} variant={getTagVariant?.(label)} maxTagWidth={maxTagWidth} />
        ))}
        {hasOverflow && (
          <>
            <TagItem
              containerProps={{ ref: buttonRef, sx: { userSelect: 'none' }, onMouseEnter, onMouseLeave: handleClose }}
              label={`+${tags.length - limit}`}
            />
            <Popover
              onClick={e => e.stopPropagation()}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  ...paperStyle,
                  maxHeight: 300,
                  maxWidth: 400,
                  overflowY: 'auto',
                  overflowX: 'auto',
                  pointerEvents: 'auto',
                  cursor: 'default',
                },
                onMouseEnter: () => {
                  isOverPopover.current = true;
                },
                onMouseLeave: () => {
                  isOverPopover.current = false;
                  setAnchorEl(null);
                },
              }}
              sx={{ pointerEvents: 'none', ml: 2 }}
              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'left' }}>
              {overflowItems.map(label => (
                <TagItem key={label} label={label} variant={getTagVariant?.(label)} />
              ))}
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};
